/**
 * TravelMaker Style Utilities
 * 
 * This file contains reusable utility functions and class patterns for consistent styling
 * across the TravelMaker application. These utilities help maintain design consistency
 * and reduce code duplication.
 */

// =============================================================================
// BUTTON UTILITIES
// =============================================================================

/**
 * Button style patterns for consistent button styling across the app
 */
export const buttonStyles = {
  // Primary button with brand colors
  primary: 'bg-primary text-white py-3 px-6 border-none rounded-md text-base font-semibold cursor-pointer transition-colors duration-300 hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed',
  
  // Secondary button with outline style
  secondary: 'border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300',
  
  // Store button wrapper for app store badges
  storeButton: 'inline-block transition-transform duration-300 ease-in-out hover:-translate-y-0.5',
  
  // Small button variant
  small: 'bg-primary text-white py-2 px-4 border-none rounded text-sm font-medium cursor-pointer transition-colors duration-300 hover:bg-primary-dark',
  
  // Large button variant
  large: 'bg-primary text-white py-4 px-8 border-none rounded-lg text-lg font-semibold cursor-pointer transition-colors duration-300 hover:bg-primary-dark'
};

/**
 * Utility function to combine button styles with additional classes
 * @param {string} variant - Button variant (primary, secondary, etc.)
 * @param {string} additionalClasses - Additional Tailwind classes
 * @returns {string} Combined class string
 */
export const getButtonClasses = (variant = 'primary', additionalClasses = '') => {
  const baseClasses = buttonStyles[variant] || buttonStyles.primary;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// CARD UTILITIES
// =============================================================================

/**
 * Card style patterns for consistent card components
 */
export const cardStyles = {
  // Basic card with shadow and padding
  basic: 'bg-white rounded-lg p-6 shadow-sm',
  
  // Feature card with hover effects
  feature: 'bg-white rounded-lg p-8 shadow-sm transition-transform duration-300 hover:-translate-y-1',
  
  // Team member card for about page
  teamMember: 'text-center',
  
  // Contact info card with gradient
  contactInfo: 'text-center p-8 bg-gradient-to-br from-primary to-primary-dark rounded-2xl text-white',
  
  // Form card with enhanced styling
  form: 'bg-white p-10 rounded-2xl shadow-xl border border-primary/10',
  
  // Mission card with background
  mission: 'bg-gray-50 py-15 px-15 rounded-lg text-center'
};

/**
 * Utility function to get card classes with optional additional styling
 * @param {string} variant - Card variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getCardClasses = (variant = 'basic', additionalClasses = '') => {
  const baseClasses = cardStyles[variant] || cardStyles.basic;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// SECTION UTILITIES
// =============================================================================

/**
 * Section layout patterns for consistent page sections
 */
export const sectionStyles = {
  // Default section with padding
  default: 'py-16 md:py-20 px-4',
  
  // Hero section with larger padding
  hero: 'py-20 md:py-32 px-4 text-center',
  
  // Content section with background
  contentWithBg: 'bg-gray-50 py-20',
  
  // CTA section with primary background
  cta: 'bg-primary text-white text-center py-20',
  
  // Footer section
  footer: 'bg-gray-800 text-white py-12',
  
  // About page sections
  aboutSection: 'mb-15'
};

/**
 * Container patterns for consistent content width and centering
 */
export const containerStyles = {
  // Default container with max width
  default: 'max-w-6xl mx-auto px-5',
  
  // Narrow container for text content
  narrow: 'max-w-3xl mx-auto px-5',
  
  // Wide container for full-width content
  wide: 'max-w-7xl mx-auto px-5',
  
  // Form container
  form: 'max-w-3xl mx-auto py-10 px-5'
};

/**
 * Utility function to get section classes
 * @param {string} variant - Section variant
 * @param {string} containerVariant - Container variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getSectionClasses = (variant = 'default', containerVariant = 'default', additionalClasses = '') => {
  const sectionClasses = sectionStyles[variant] || sectionStyles.default;
  const containerClasses = containerStyles[containerVariant] || containerStyles.default;
  const combined = `${sectionClasses} ${containerClasses}`;
  return additionalClasses ? `${combined} ${additionalClasses}` : combined;
};

// =============================================================================
// GRID UTILITIES
// =============================================================================

/**
 * Grid layout patterns for responsive grids
 */
export const gridStyles = {
  // Features grid (1-2-3 columns)
  features: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8',
  
  // Screenshots grid (1-2-4 columns)
  screenshots: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6',
  
  // Team grid (1-2-3 columns)
  team: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8',
  
  // Footer grid (1-2-4 columns)
  footer: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8',
  
  // Two column grid
  twoColumn: 'grid grid-cols-1 md:grid-cols-2 gap-10',
  
  // Hero grid (responsive)
  hero: 'grid grid-cols-2 items-start overflow-hidden p-0 md:grid-cols-2 md:h-screen md:p-0 max-md:grid-cols-1 max-md:h-auto max-md:py-10'
};

/**
 * Utility function to get grid classes
 * @param {string} variant - Grid variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getGridClasses = (variant = 'features', additionalClasses = '') => {
  const baseClasses = gridStyles[variant] || gridStyles.features;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// TYPOGRAPHY UTILITIES
// =============================================================================

/**
 * Typography patterns for consistent text styling
 */
export const typographyStyles = {
  // Page titles
  pageTitle: 'text-4xl mb-5 text-gray-800',
  
  // Hero title
  heroTitle: 'text-6xl mb-6 text-gray-800 font-bold leading-tight md:text-6xl max-md:text-4xl',
  
  // Section titles
  sectionTitle: 'text-center mb-12 text-2xl text-gray-800',
  
  // Subsection titles
  subsectionTitle: 'text-2xl mb-8 text-gray-800',
  
  // Card titles
  cardTitle: 'mb-4 text-xl',
  
  // Hero subtitle
  heroSubtitle: 'text-xl mb-12 text-gray-600 max-w-lg leading-relaxed',
  
  // Body text
  bodyText: 'text-gray-600 leading-relaxed',
  
  // Large body text
  bodyTextLarge: 'text-xl text-gray-600 max-w-3xl mx-auto',
  
  // Mission text
  missionText: 'text-xl italic max-w-3xl mx-auto text-gray-600'
};

/**
 * Utility function to get typography classes
 * @param {string} variant - Typography variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getTypographyClasses = (variant = 'bodyText', additionalClasses = '') => {
  const baseClasses = typographyStyles[variant] || typographyStyles.bodyText;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// FORM UTILITIES
// =============================================================================

/**
 * Form element patterns for consistent form styling
 */
export const formStyles = {
  // Input field
  input: 'w-full py-3 px-4 border border-gray-300 rounded-md text-base transition-colors duration-300 focus:border-primary focus:outline-none',
  
  // Textarea field
  textarea: 'w-full py-3 px-4 border border-gray-300 rounded-md text-base min-h-[150px] resize-y transition-colors duration-300 focus:border-primary focus:outline-none',
  
  // Label
  label: 'block mb-2 font-medium text-gray-800',
  
  // Form group
  formGroup: 'mb-5',
  
  // Success message
  successMessage: 'bg-green-100 text-green-800 p-4 rounded-md mb-5',
  
  // Error message
  errorMessage: 'bg-red-100 text-red-800 p-4 rounded-md mb-5'
};

/**
 * Utility function to get form classes
 * @param {string} variant - Form element variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getFormClasses = (variant = 'input', additionalClasses = '') => {
  const baseClasses = formStyles[variant] || formStyles.input;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// IMAGE UTILITIES
// =============================================================================

/**
 * Image patterns for consistent image styling
 */
export const imageStyles = {
  // Team member avatar
  avatar: 'w-38 h-38 rounded-full overflow-hidden mx-auto mb-5',
  
  // Screenshot image
  screenshot: 'w-full h-auto block transition-transform duration-300 hover:scale-105',
  
  // About page image
  aboutImage: 'w-full rounded-lg shadow-lg',
  
  // Store badge
  storeBadge: 'h-15 w-auto block',
  
  // Logo image
  logo: 'w-10 h-10 object-contain',
  
  // Film reel image
  filmImage: 'w-full h-full object-cover block'
};

/**
 * Utility function to get image classes
 * @param {string} variant - Image variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getImageClasses = (variant = 'screenshot', additionalClasses = '') => {
  const baseClasses = imageStyles[variant] || imageStyles.screenshot;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// ANIMATION UTILITIES
// =============================================================================

/**
 * Animation patterns for consistent hover and transition effects
 */
export const animationStyles = {
  // Hover lift effect
  hoverLift: 'transition-transform duration-300 hover:-translate-y-1',
  
  // Hover scale effect
  hoverScale: 'transition-transform duration-300 hover:scale-105',
  
  // Hover button lift
  hoverButtonLift: 'transition-transform duration-300 ease-in-out hover:-translate-y-0.5',
  
  // Color transition
  colorTransition: 'transition-colors duration-300',
  
  // Opacity transition
  opacityTransition: 'transition-opacity duration-300',
  
  // All transitions
  allTransition: 'transition-all duration-300'
};

/**
 * Utility function to get animation classes
 * @param {string} variant - Animation variant
 * @param {string} additionalClasses - Additional classes
 * @returns {string} Combined class string
 */
export const getAnimationClasses = (variant = 'colorTransition', additionalClasses = '') => {
  const baseClasses = animationStyles[variant] || animationStyles.colorTransition;
  return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses;
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Utility function to conditionally combine classes
 * @param {...string} classes - Classes to combine
 * @returns {string} Combined class string
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Utility function to create responsive classes
 * @param {Object} breakpoints - Object with breakpoint keys and class values
 * @returns {string} Responsive class string
 */
export const responsive = (breakpoints) => {
  const { base = '', sm = '', md = '', lg = '', xl = '' } = breakpoints;
  return [base, sm && `sm:${sm}`, md && `md:${md}`, lg && `lg:${lg}`, xl && `xl:${xl}`]
    .filter(Boolean)
    .join(' ');
};

/**
 * Common class combinations for frequently used patterns
 */
export const commonPatterns = {
  // Centered content
  centered: 'flex items-center justify-center',
  
  // Flex column centered
  flexColCentered: 'flex flex-col items-center justify-center',
  
  // Full width and height
  fullSize: 'w-full h-full',
  
  // Absolute center
  absoluteCenter: 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
  
  // Screen height
  screenHeight: 'h-screen',
  
  // Hidden on mobile, visible on desktop
  desktopOnly: 'hidden md:block',
  
  // Visible on mobile, hidden on desktop
  mobileOnly: 'block md:hidden',
  
  // Responsive flex direction
  responsiveFlex: 'flex flex-col md:flex-row',
  
  // Button group spacing
  buttonGroup: 'flex gap-6 md:flex md:gap-6 max-md:justify-center max-md:flex-col max-md:items-center'
};